import { html } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND_SCAN_TASKS_FORM } from '../../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { FormGroup } from '../../../../../../../../../../ui/gzForm/formGroup/form-group';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import {
  AntimalwareOnDemandScanTask,
  AntimalwareOnDemandScanTaskScanType,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzHardCodedFormField } from 'apps/webcomponents/src/ui/gzForm/formField/component/GzHardCodedFormField';
import {
  WeekDaysValues,
  WeekDayValueToScheduleFieldName,
} from '../../../../../../../../../common/policyPanel/uiSettings/schedulerTemplate/types';
import _ from 'lodash';
import { getAntimalwareOnDemandScanTaskDefaultValue } from '../../../../../../../utils/uiSettings/antimalware/antimalware.values';
import { GzAntimalwareOnDemandScanTaskOptionsFormWebComponent } from '../../../../forms/optionsForm';
import { GzAntimalwareOnDemandScanTaskGeneralFormWebComponent } from '../../../../forms/generalForm';
import { GzAntimalwareOnDemandScanTaskTargetFormWebComponent } from '../../../../forms/targetForm';
import { convertScanTaskStartDateUiValueToDbValue } from '../../../../forms/generalForm/utils/converters';
import { FontSizes, FontWeights } from '../../../../../../../../../../types/common/types';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND_SCAN_TASKS_FORM)
export class GzAntimalwareOnDemandScanTasksFormWebComponent extends GzPolicyFullPageWindowFormWebComponent<AntimalwareOnDemandScanTask> {
  // general section
  @query('gz-policy-panel-antimalware-on-demand-scan-task-general-form')
  private generalForm: GzAntimalwareOnDemandScanTaskGeneralFormWebComponent;

  // options section
  @query('gz-policy-panel-antimalware-on-demand-scan-task-options-form')
  private optionsForm: GzAntimalwareOnDemandScanTaskOptionsFormWebComponent;

  // target section
  @query('gz-policy-panel-antimalware-on-demand-scan-task-target-form')
  private targetForm: GzAntimalwareOnDemandScanTaskTargetFormWebComponent;

  public constructor(private scanType: AntimalwareOnDemandScanTaskScanType, value?: AntimalwareOnDemandScanTask) {
    super(value);
  }

  static get styles() {
    return styles;
  }

  public setError(error: string) {
    this.generalForm.setError(error);
  }

  protected getConvertedFormValue() {
    const formValue = _.cloneDeep(this.formGroup.value);

    // set general form values
    const generalForm = _.get(formValue, ['generalForm'], {});
    _.merge(formValue, generalForm);
    _.unset(formValue, ['generalForm']);

    // set options form values
    const optionsForm = _.get(formValue, ['optionsForm'], {});
    _.merge(formValue, optionsForm);
    _.unset(formValue, ['optionsForm']);

    // set target form values
    const targetForm = _.get(formValue, ['targetForm'], {});
    _.merge(formValue, targetForm);
    _.unset(formValue, ['targetForm']);

    // convert scheduler
    const startDateUiValue = _.get(formValue, ['scheduler', 'startDate'], {});
    const startDateDbValue = convertScanTaskStartDateUiValueToDbValue(startDateUiValue);
    _.set(formValue, ['scheduler', 'startDate'], startDateDbValue);

    // compute week days fields from form value
    const weekdays = _.get(formValue, ['scheduler', 'recurrentDays'], []);
    WeekDaysValues.forEach(day =>
      _.set(formValue, ['scheduler', WeekDayValueToScheduleFieldName[day]], weekdays.includes(day) ? 1 : 0)
    );
    _.unset(formValue, ['scheduler', 'recurrentDays']);

    // fix null value of scannerId if exists
    if (_.get(formValue, ['scanSettings', 'scannerId']) === null) {
      _.set(formValue, ['scanSettings', 'scannerId'], '');
    }

    return formValue;
  }

  async createFormGroup() {
    this.formGroup = FormGroup.build({
      scanTaskHash: new GzHardCodedFormField<string>(''), // this value will be set by backend
      uniqueId: new GzHardCodedFormField<string>(''),
      scanType: new GzHardCodedFormField<number>(this.scanType),
    });

    await this.optionsForm.updateComplete;
    this.formGroup.addFormChild(['optionsForm'], this.optionsForm.formGroup);

    await this.generalForm.updateComplete;
    this.formGroup.addFormChild(['generalForm'], this.generalForm.formGroup);

    await this.targetForm.updateComplete;
    this.formGroup.addFormChild(['targetForm'], this.targetForm.formGroup);
  }

  protected async setFormGroupValue() {
    let valueToBeSet;
    if (!this.value) {
      valueToBeSet = getAntimalwareOnDemandScanTaskDefaultValue(this.scanType) as any;
    } else {
      valueToBeSet = this.value;
    }

    this.generalForm.setFormGroupValue(this.scanType, {
      taskName: valueToBeSet.taskName,
      recovery: valueToBeSet.recovery,
      scheduler: valueToBeSet.scheduler,
      scanSettings: {
        miscellaneous: {
          runTask: valueToBeSet.scanSettings.miscellaneous.runTask,
          shutDownWhenFinished: valueToBeSet.scanSettings.miscellaneous.shutDownWhenFinished,
        },
        scannerId: valueToBeSet.scanSettings.scannerId,
      },
      pauseWhenOnBattery: valueToBeSet.pauseWhenOnBattery,
      cpuUsageControl: valueToBeSet.cpuUsageControl,
    });
    this.optionsForm.setFormGroupValue(this.scanType, {
      scanProfile: valueToBeSet.scanProfile,
      scanSettings: {
        fileTypes: valueToBeSet.scanSettings.fileTypes,
        extensions: valueToBeSet.scanSettings.extensions,
        archives: valueToBeSet.scanSettings.archives,
        miscellaneous: valueToBeSet.scanSettings.miscellaneous,
        action: valueToBeSet.scanSettings.action,
      },
    });
    this.targetForm.setFormGroupValue(this.scanType, {
      scanSettings: {
        networkCredentials: valueToBeSet.scanSettings.networkCredentials,
      },
      scanTarget: valueToBeSet.scanTarget,
      exclusions: valueToBeSet.exclusions,
    });

    this.formGroup.value = valueToBeSet;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div class="tasks-form-container">
        <!-- General section -->
        <div id="tasks-general-section">
          <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize="${FontSizes.HEADING_4}" class="mt-4"
            >${translateFn('General')}</gz-title
          >

          <gz-policy-panel-antimalware-on-demand-scan-task-general-form>
          </gz-policy-panel-antimalware-on-demand-scan-task-general-form>
        </div>

        <!-- Options section -->
        <div id="tasks-options-section">
          <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize="${FontSizes.HEADING_4}" class="mt-4"
            >${translateFn('Options')}</gz-title
          >

          <gz-policy-panel-antimalware-on-demand-scan-task-options-form>
          </gz-policy-panel-antimalware-on-demand-scan-task-options-form>
        </div>

        <!-- Target section -->
        <div id="tasks-target-section">
          <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize="${FontSizes.HEADING_4}" class="mt-4"
            >${translateFn('Target')}</gz-title
          >

          <gz-policy-panel-antimalware-on-demand-scan-task-target-form>
          </gz-policy-panel-antimalware-on-demand-scan-task-target-form>
        </div>
      </div>
    `;
  }
}
